import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useIsLoggedIn from '../../../isLoggedIn';
import { publicSummary, s3Overview, s3OverviewAdmin } from './architectureModel';
import { selectCurrentUser } from '../../../slices/userSlice';
import {
  useArchitectureCounts, useAdminNotes, useArchitectureStructure, withCounts, withAdminNotes,
  withNoteField
} from './useArchitectureCounts';
import './architectureExplorer.css';

/**
 * ArchitectureExplorer — a paged, expandable map of the system chain.
 *
 * ⭐ BUILT FOR THE READER WHO CANNOT HOLD IT ALL. The system passed the size where one person
 * remembers every chain, so the design goal is not completeness on one screen — it is being able
 * to FIND one branch and read the warnings attached to it. Hence: pages rather than a wall,
 * collapsed by default, and every hard-won caveat sitting on the node it belongs to rather than
 * in a preamble nobody reads twice.
 *
 * All content lives in architectureModel.js. Extending the picture is a data edit.
 */

const PAGES = [
  { id: 'book-creator', label: 'Book Creator', blurb: 'From a PDF to finished chapter audio.' },
  { id: 'voice-actor', label: 'Voice Actor', blurb: 'Portfolio, applications, and recording.' },
  { id: 'shared', label: 'Where they meet', blurb: 'The marketplace, and the things kept apart from the main structure.' },
  { id: 'storage', label: 'Storage', blurb: 'Storage overview.' },
  { id: 'sequences', label: 'Sequences', blurb: 'The operating order, step by step.' }
];

const Node = ({ node, depth = 0, onJump, schema, kinds }) => {
  // Deep branches start collapsed: the value is in finding a branch, not in facing all of them.
  const [open, setOpen] = useState(depth < 2);
  const kids = node.children || [];
  const kind = (kinds && (kinds[node.kind] || kinds.entity)) || { hint: '' };

  // ⭐ Internal class names are meaningless to a reader. An admin needs the real one to match what
  // the database shows them, so `schema` swaps between the two registers rather than making one
  // audience read the other's vocabulary.
  // ⚠️ No example class names in this comment — they would be the very thing this file no longer
  // carries, and the guard test scans comments along with code.
  const shown = schema ? node.label : (node.publicLabel || node.label);

  return (
    <li className={`aae-node aae-depth-${Math.min(depth, 4)}`}>
      <div className="aae-node-row">
        {kids.length > 0 ? (
          <button
            type="button"
            className="aae-toggle"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            title={open ? 'Collapse' : 'Expand'}
          >
            {open ? '−' : '+'}
          </button>
        ) : <span className="aae-toggle aae-toggle-leaf" aria-hidden="true" />}

        <span className={`aae-chip aae-kind-${node.kind || 'entity'}`} title={kind.hint}>
          {shown}
        </span>

        {/* ⛔ Raw Cypher relationship names. Beside every node on every page, and meaningless to a
            reader — genuinely useful to us. Shown only when the admin payload confirmed. */}
        {schema && node.rel && <code className="aae-rel">:{node.rel}</code>}
        {/* A number is present ONLY when the server counted it for the selected series — the model
            ships every count as null. So the tooltip can state that unconditionally; it no longer
            claims a fixed snapshot date it cannot know. */}
        {typeof node.count === 'number' && (
          <span className="aae-count" title="Rows in the selected series, counted just now">
            {node.count.toLocaleString()}
          </span>
        )}
        {node.ref && (
          <button type="button" className="aae-jump" onClick={() => onJump && onJump(node.ref)}>
            see detail →
          </button>
        )}
      </div>

      {node.note && <p className="aae-note">{node.note}</p>}
      {/* ⭐ The creator tier: what will BITE someone operating this, in the vocabulary of the
          screens they use. Arrives with the structure, so every signed-in reader has it and an
          anonymous one has nothing to reveal. Ordered ABOVE the admin note deliberately — an admin
          sees both, and the operating warning is the one that changes what they do next. */}
      {node.creatorNote && <p className="aae-note aae-note-creator">{node.creatorNote}</p>}
      {/* Present only when the server sent one. Visually distinct so an admin can tell at a glance
          which half of an explanation the public is not reading. */}
      {node.adminNote && <p className="aae-note aae-note-admin">{node.adminNote}</p>}

      {open && kids.length > 0 && (
        <ul className="aae-children">
          {kids.map((c) => (
            <Node key={c.id} node={c} depth={depth + 1} onJump={onJump} schema={schema} kinds={kinds} />
          ))}
        </ul>
      )}
    </li>
  );
};

const Tree = ({ root, onJump, schema, kinds }) => (
  <ul className="aae-tree">
    <Node node={root} onJump={onJump} schema={schema} kinds={kinds} />
  </ul>
);

/**
 * ⭐ THE MOST-READ TEXT IN THE EMBED — it appears on three of the five pages. The public hints say
 * what a kind MEANS; `kindNotes` adds the graph vocabulary for admins, so neither audience is
 * reading the other's language.
 */
const Legend = ({ kindNotes, kinds }) => (
  <div className="aae-legend">
    {Object.entries(kinds || {}).map(([k, v]) => (
      <span key={k} className="aae-legend-item">
        <span className={`aae-chip aae-kind-${k}`}>{v.label}</span>
        <span className="aae-legend-hint">
          {v.hint}
          {kindNotes && kindNotes[k] && (
            <span className="aae-legend-admin">{kindNotes[k]}</span>
          )}
        </span>
      </span>
    ))}
  </div>
);

/**
 * Two modes, and the difference is only whether numbers are present.
 *
 *   GENERIC  — not signed in, or `counts=off`. Structure only. Asks the server for nothing.
 *   LIVE     — signed in. A series selector appears and counts are fetched for the chosen one.
 *
 * ⭐ The structure is IDENTICAL in both, so the article reads the same to either audience and the
 * generic view is not a degraded version of anything — it is the diagram, without numbers.
 */
const ArchitectureExplorer = ({ view = 'book-creator', counts: countsParam }) => {
  const [page, setPage] = useState(
    PAGES.some((p) => p.id === view) ? view : 'book-creator'
  );
  const [detail, setDetail] = useState(null);

  const currentUser = useSelector(selectCurrentUser);
  /**
   * ⛔⛔ THE TOKEN, NOT THE CACHED USER (2026-10-24). Both gates in this component asked
   * "do we know who the user is?" when the question is "do we hold a live credential?" — and the
   * two came apart the moment a token expired in an open tab: `currentUser` is in-memory Redux
   * and survived the token being cleaned up, so the counts fetch fired with no Authorization
   * header and the operator got a logout panel for an ordinary 12-hour expiry.
   *
   * ⓘ `useIsLoggedIn` reads the token’s OWN expiry, which is the only thing that can answer
   * this. It is declared here rather than beside the other gate below, because `enabled` is
   * computed above that line and a const cannot be read before it is initialised.
   */
  const tokenLive = useIsLoggedIn();
  // `counts=off` lets a genuinely public explainer stay structural even for a signed-in reader.
  const wantsCounts = String(countsParam || '').toLowerCase() !== 'off';
  const enabled = wantsCounts && !!tokenLive && !!(currentUser && currentUser.userId);

  /**
   * ⚠️ AN OPTIMISATION, NOT THE PROTECTION. This only avoids firing a request that would 403; the
   * server's `requireAdmin` on /api/architecture/admin-detail is what decides entitlement, and the
   * content is no longer in the bundle for this flag to reveal.
   *
   * That was not true a commit ago: the notes were a constant in architectureModel.js and this
   * check chose whether to DRAW them, which left the text delivered to every visitor of a public
   * article and readable in devtools. Tampering with this boolean now yields an empty footer.
   *
   * Role shape matches MessageProvider and SecurityService: `primaryRole`, then `role`, then the
   * `roles` array — a user can carry admin in the array without it being their primary.
   */
  const isAdminReader = useMemo(() => {
    if (!currentUser) return false;
    const primary = currentUser.primaryRole || currentUser.role;
    if (primary === 'admin') return true;
    return Array.isArray(currentUser.roles) && currentUser.roles.includes('admin');
  }, [currentUser]);

  const live = useArchitectureCounts({ enabled });
  const adminDetail = useAdminNotes({ enabled: isAdminReader });

  /**
   * ⛔ THE STRUCTURE IS FETCHED, NOT BUNDLED. Signed in gets the map; a stranger gets the blurb
   * below and nothing else, because there is nothing else in the bundle to reveal.
   */
  // ⚠️ The same correction: a cached user is not a credential.
  const signedIn = !!tokenLive && !!(currentUser && currentUser.userId);
  const { structure } = useArchitectureStructure({ enabled: signedIn });

  /**
   * The creator tier, riding in with the structure.
   *
   * ⭐ NOT A SEPARATE FETCH and not a separate role check. These notes are for the person who
   * OPERATES the pipeline, which is everyone signed in far more often than it is an admin, so they
   * arrive with the map rather than behind a second endpoint that would have to be entitled again.
   *
   * ⚠️ Memoised with a stable fallback. Reading `structure?.creatorNotes || {}` inline would mint a
   * fresh object every render and make `decorate` below re-derive all three trees each time.
   */
  const creatorNotes = useMemo(() => ({
    nodeNotes: structure?.creatorNotes?.nodeNotes || {},
    sequenceNotes: structure?.creatorNotes?.sequenceNotes || {}
  }), [structure]);

  /**
   * ⭐ SERVER-CONFIRMED, NOT CLIENT-GUESSED — and that distinction is the whole reason this is a
   * separate value from `isAdminReader`. This is true only because the admin endpoint actually
   * ANSWERED, so it cannot be turned on by editing a role in devtools: without the payload there is
   * no schema vocabulary to reveal.
   *
   * It gates the schema register — real class names instead of plain labels, `:REL` chips, the
   * standalone `key` column, the graph version of each sequence step. All of that is language for
   * us; a reader is served worse by seeing it, not better.
   */
  const adminConfirmed = !!adminDetail.storage;

  // ⚠️ Memoised on `live.counts`, not on the trees: the model objects are module-level constants
  // and withCounts must never write into them (see the note there).
  // THREE independent merges, composed: counts by id, then the creator tier, then the admin tier.
  // Each returns new objects, so the module-level model constants are never written into.
  //
  // ⭐ Both tiers are merged unconditionally and the ENDPOINTS decide what arrived. A creator's
  // admin map is `{}` because requireAdmin refused it; a stranger's creator map is `{}` because
  // the structure fetch needed a token. No client-side role test chooses what to draw — which is
  // the whole lesson of this component's history, applied to the new tier as well as the old.
  const decorate = useMemo(() => (
    (tree) => (tree
      ? withAdminNotes(
        withNoteField(withCounts(tree, live.counts), creatorNotes.nodeNotes, 'creatorNote'),
        adminDetail.nodeNotes
      )
      : null)
  ), [live.counts, creatorNotes.nodeNotes, adminDetail.nodeNotes]);

  const bcTree = useMemo(() => decorate(structure?.bookCreatorTree), [decorate, structure]);
  const vaTree = useMemo(() => decorate(structure?.voiceActorTree), [decorate, structure]);
  const oppTree = useMemo(() => decorate(structure?.opportunityTree), [decorate, structure]);
  const detailTrees = structure?.detailTrees || {};
  const detailTree = useMemo(
    () => (detail && detailTrees[detail] ? decorate(detailTrees[detail]) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detail, decorate, structure]
  );

  const current = PAGES.find((p) => p.id === page) || PAGES[0];

  /**
   * ⛔ THE PUBLIC VIEW IS THE WHOLE VIEW FOR A STRANGER. No tabs, no tree, no sequences — because
   * there is no structure in the bundle to draw. Returning early also means the pages below cannot
   * accidentally render an empty shell of the real thing, which would advertise its shape.
   *
   * ⚠️ Also the state a signed-in reader sees if the fetch fails. Failing to the public blurb is
   * the right direction to fail in.
   */
  if (!structure) {
    return (
      <section className="aae" aria-label="AuthoratorAI overview">
        <header className="aae-head">
          <div>
            <h3 className="aae-title">How AuthoratorAI works</h3>
          </div>
        </header>
        <p className="aae-blurb">{publicSummary.blurb}</p>
        <ul className="aae-overview-points">
          {publicSummary.points.map((pt) => <li key={pt.slice(0, 24)}>{pt}</li>)}
        </ul>
      </section>
    );
  }

  return (
    <section className="aae" aria-label="AuthoratorAI architecture explorer">
      <header className="aae-head">
        <div>
          <h3 className="aae-title">System chain</h3>
          {/* ⛔ The subtitle must describe the state it is actually in. It previously claimed a
              fixed "derived on 2026-09-09" date in every mode, which was a lie in four of the
              five states below. */}
          <p className="aae-sub">
            {live.status === 'ready' && (
              <>
                Counted now for <strong>{live.litGroupName}</strong>
                {live.partial && ' — some detail counts were unavailable'}.
              </>
            )}
            {live.status === 'loading' && 'Counting…'}
            {live.status === 'error' && (
              <span className="aae-sub-warn">
                Counts unavailable ({live.error}). The structure below is unaffected.
              </span>
            )}
            {live.status === 'empty' && 'No series on this account yet, so there is nothing to count.'}
            {live.status === 'off' && 'The structure of the system. Relationships and storage paths.'}
          </p>
        </div>

        {/* Selector only where there is something to choose. */}
        {live.status !== 'off' && live.litGroups.length > 1 && (
          <label className="aae-picker">
            <span>Series</span>
            <select
              value={live.selected || ''}
              onChange={(e) => live.select(e.target.value)}
              disabled={live.status === 'loading'}
            >
              {live.litGroups.map((g) => (
                <option key={g.uuid} value={g.uuid}>{g.name}</option>
              ))}
            </select>
          </label>
        )}
      </header>

      <nav className="aae-tabs" role="tablist">
        {PAGES.map((p) => (
          <button
            key={p.id}
            role="tab"
            type="button"
            aria-selected={page === p.id}
            className={`aae-tab ${page === p.id ? 'is-active' : ''}`}
            onClick={() => { setPage(p.id); setDetail(null); }}
          >
            {p.label}
          </button>
        ))}
      </nav>

      {/* A heading rather than body text, so the page's subject stands out.
          ⚠️ NOT wrapped in <p>: a heading cannot be a descendant of a paragraph. React warns
          (validateDOMNesting), and .aae-blurb's 0.9em dim styling would have shrunk and greyed the
          very heading the wrapper was added to emphasise. */}
      <h4 className="aae-h4">{current.blurb}</h4>

      {(page === 'book-creator' || page === 'voice-actor' || page === 'shared') && <Legend kindNotes={adminDetail.kindNotes} kinds={structure?.KINDS} />}

      {page === 'book-creator' && (
        <>
          <Tree root={bcTree} onJump={setDetail} schema={adminConfirmed} kinds={structure?.KINDS} />
          {detail && detailTrees[detail] && (
            <div className="aae-detail">
              <div className="aae-detail-head">
                <strong>Detail — {detailTrees[detail].label}</strong>
                <button type="button" className="aae-close" onClick={() => setDetail(null)}>close</button>
              </div>
              <Tree root={detailTree} schema={adminConfirmed} kinds={structure?.KINDS} />
            </div>
          )}
          {/* ⚠️ No node names in this hint. It lives in the bundle, so naming the two branches
              would put back exactly what moved out. The "see detail →" buttons carry the labels. */}
          {!detail && (
            <p className="aae-hint">
              Two branches carry most of the complexity — open either “see detail →” to follow them.
            </p>
          )}
        </>
      )}

      {page === 'voice-actor' && <Tree root={vaTree} schema={adminConfirmed} kinds={structure?.KINDS} />}

      {page === 'shared' && (
        <>
          <Tree root={oppTree} schema={adminConfirmed} kinds={structure?.KINDS} />
          <h4 className="aae-h4">Kept apart from the rest</h4>
          <p className="aae-blurb">
            ⛔ These have <strong>no relationships at all</strong>. No traversal reaches them, and a
            “nodes with no edges” query calls every one an orphan — including the most expensive
            thing in the database.
          </p>
          <ul className="aae-standalone">
            {(structure?.standaloneNodes || []).map((s) => (
              <li key={s.label}>
                <div className="aae-node-row">
                  <span className="aae-chip aae-kind-standalone">{s.label}</span>
                  {/* The joining property — `bookId` and the like. Pure schema, so admin-only and
                      supplied by the server rather than sitting in the model. */}
                  {adminDetail.standaloneKeys[s.label] && (
                    <code className="aae-rel">{adminDetail.standaloneKeys[s.label]}</code>
                  )}
                  {/* ⛔ MUST BE GUARDED. These counts are null in the generic (not-signed-in)
                      view, and an unguarded .toLocaleString() on null throws — taking the whole
                      article down, not just the number. The node renderer above already guards
                      with `typeof === 'number'`; this one did not. */}
                  {typeof s.count === 'number' && (
                    <span className="aae-count">{s.count.toLocaleString()}</span>
                  )}
                  {s.size && <span className="aae-size">{s.size}</span>}
                </div>
                <p className="aae-note">{s.note}</p>
                {/* Keyed by label, since these entries have no id. */}
                {adminDetail.standaloneNotes[s.label] && (
                  <p className="aae-note aae-note-admin">{adminDetail.standaloneNotes[s.label]}</p>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {page === 'storage' && (
        <>
          {/* PUBLIC: describes the arrangement without naming a bucket, a prefix or a key
              template. This is the whole page for anyone who is not an admin. */}
          {adminDetail.storage && (<div className='aae-blurb-container'>
            <p className="aae-blurb">{s3OverviewAdmin.blurb}</p>
            <ul className="aae-overview-points">
              {s3OverviewAdmin.points.map((pt) => <li key={pt.slice(0, 24)}>{pt}</li>)}
            </ul>

          </div>)}

          {!adminDetail.storage && (<div className='aae-blurb-container'>
            <p className="aae-blurb">{s3Overview.blurb}</p>
            <ul className="aae-overview-points">
              {s3Overview.points.map((pt) => <li key={pt.slice(0, 24)}>{pt}</li>)}
            </ul>
          </div>)}

          {/* ⛔ ADMIN ONLY, AND ABSENT RATHER THAN HIDDEN. `adminDetail.storage` is null for
              anyone the server refused, and the paths are not in the bundle for a devtools reader
              to find — which is the difference between this and a conditional render over a
              client constant. */}
          {adminDetail.storage && (
            <div className="aae-admin-block">
              <h4 className="aae-h4">
                Registry paths
                <span className="aae-admin-tag" title="Served to admins only — not in the public bundle">
                  admin
                </span>
              </h4>
              <p className="aae-blurb">{adminDetail.storage.intro}</p>
              <div className="aae-roots">
                {(adminDetail.storage.roots || []).map((r) => (
                  <div key={r.prefix} className="aae-root">
                    <code>{r.prefix}</code><span>{r.note}</span>
                  </div>
                ))}
              </div>
              <ul className="aae-paths">
                {(adminDetail.storage.paths || []).map((p) => (
                  <li key={p.entry}>
                    <div className="aae-path-entry">{p.entry}</div>
                    <code className="aae-path">{p.path}</code>
                    {p.note && <p className="aae-note">{p.note}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {page === 'sequences' && (
        <div className="aae-seqs">
          {(structure?.sequences || []).map((s) => (
            <div key={s.id} className="aae-seq">
              <div className="aae-seq-head">
                <strong>{s.title}</strong>
                <span className="aae-seq-actor">{s.actor}</span>
              </div>
              <ol className="aae-steps">
                {s.steps.map((st) => (
                  <li key={st.n}>
                    <span className="aae-step-do">{st.do}</span>
                    {/* Plain language for a reader: what the step ACHIEVES. */}
                    {st.makes && <span className="aae-step-makes">→ {st.makes}</span>}
                    {/* The node-and-edge version of the same thing, for us. */}
                    {adminDetail.sequenceMakes[`${s.id}.${st.n}`] && (
                      <code className="aae-step-schema">
                        {adminDetail.sequenceMakes[`${s.id}.${st.n}`]}
                      </code>
                    )}
                    {st.s3 && <code className="aae-step-s3">{st.s3}</code>}
                    {st.note && <p className="aae-note">{st.note}</p>}
                    {/* ⭐ Creator tier first: this is the operating manual, and it was admin-gated
                        until now — which put it in front of the one role that never runs the
                        pipeline. Keyed `<sequenceId>.<step n>`, like the admin notes. */}
                    {creatorNotes.sequenceNotes[`${s.id}.${st.n}`] && (
                      <p className="aae-note aae-note-creator">
                        {creatorNotes.sequenceNotes[`${s.id}.${st.n}`]}
                      </p>
                    )}
                    {/* Keyed `<sequenceId>.<step n>` — steps have no id of their own. */}
                    {adminDetail.sequenceNotes[`${s.id}.${st.n}`] && (
                      <p className="aae-note aae-note-admin">
                        {adminDetail.sequenceNotes[`${s.id}.${st.n}`]}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
              {/* ── Sequence-level caveat block ───────────────────────────────────────────────
                *
                * ⭐ Sits BELOW the steps because it is about their ORDER, not about any one of
                * them. A step note pinned to step 1 would be invisible to an operator who came
                * to read step 4, and the cost of the wrong order is paid before step 4 exists.
                *
                * ⭐ COMES FROM `structure`, NOT `adminDetail` — so a BOOK CREATOR sees it. They
                * are the person who runs this sequence; an admin only reads about it. It was
                * admin-gated for one commit, which put the warning everywhere except in front of
                * the person it protects.
                *
                * ⛔ Still not public: /architecture/structure requires a token, so an anonymous
                * reader of the article receives no caveats and the bundle carries none. Absent
                * rather than hidden, and `{}` is also what a failed fetch looks like, so there is
                * no state in which a reader sees a placeholder hinting at withheld text.
                *
                * ⓘ No "Admin only" tag here, unlike the notes below — it would now be a lie. */}
              {(structure?.sequenceCaveats || {})[s.id] && (
                <div className="aae-seq-caveat">
                  <div className="aae-seq-caveat-head">
                    <strong>{structure.sequenceCaveats[s.id].title}</strong>
                  </div>
                  {(structure.sequenceCaveats[s.id].points || []).map((p, i) => (
                    <p key={i} className="aae-note aae-caveat-point">{p}</p>
                  ))}
                  {structure.sequenceCaveats[s.id].order && (
                    <p className="aae-seq-caveat-order">
                      {structure.sequenceCaveats[s.id].order}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Admin-only editorial notes. Labelled in the UI as well as gated, so an admin reading the
          published article knows which part of it the public cannot see — otherwise it is easy to
          quote internal lore at someone who has never been shown it. */}
      {/* ⭐ Renders only what the SERVER sent. `adminNotes` is empty for anyone the endpoint
          refused, and empty is also what a failure looks like — so there is no state in which a
          non-admin sees a placeholder hinting at what is hidden. The headings come from the server
          too, which is why no admin wording is hard-coded here. */}
      {adminDetail.noteSections.length > 0 && (
        <footer className="aae-foot">
          {adminDetail.noteSections.map((section) => (
            <div key={section.id}>
              <h4 className="aae-h4">
                {section.heading}
                <span className="aae-admin-tag" title="Served to admins only — not in the public bundle">
                  admin
                </span>
              </h4>
              <ul className="aae-pitfalls">
                {(section.items || []).map((p) => <li key={p.slice(0, 32)}>{p}</li>)}
              </ul>
            </div>
          ))}
        </footer>
      )}
    </section>
  );
};

export default ArchitectureExplorer;
